import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Loader } from '@components/shared/Loader/Loader';
import { useInjection } from '@context/inversify-context-provider';
import { OOConfiguration } from '@features/oneOnboarding/interfaces';
import { serializeFilters } from '@features/oneOnboardingAdmin/pages/AdminDashboard/adminHelpers';
import { TYPES } from '@libs/InversifyType';
import { CheckUser } from '@models/user.model';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Drawer, Grid, IconButton } from '@mui/material';
import { RootState } from '@store/rootReducer';
import { oneOnboardingSlice } from '@store/slices/oneOnboarding.slice';
import { snackbarSlice } from '@store/slices/snackbar';

import { AamBackendApi } from '../../../../../libs/aamBackendApi';
import {
  useSiteOptions,
  useUpdateQueryStringValueWithoutNavigation,
} from '../../../components/DashboardFilters/dashBoardFilterHooks';
import useUpdateAdminDashboardFilters from '../../../components/DashboardFilters/dashboardFiltersHooks';
import { bulkMailPayload, MailHistoryType } from '../BulkMailInterfaces';

import BulkMailList from './BulkMailList';
import EmailPlaceholderList from './EmailPlaceholderList';
import MailPreview from './MailPreview';

export interface OOBulkEmailProps {
  selectedConfiguration?: OOConfiguration;
  allJourneys: any[];
  allClientOrganizations: any[];
  onClientOrganizationChange: (clientOrganisationId: any) => void;
  onJourneyChange: (journeyId: any) => void;
  filterControls: any;
  selectedValues: Record<string, any>;
  bulkEmailSlider: boolean;
  selectedUsers: CheckUser[];
}
export const BulkEmail: FC<OOBulkEmailProps> = ({
  selectedConfiguration,
  allClientOrganizations,
  allJourneys,
  onClientOrganizationChange,
  onJourneyChange,
  filterControls,
  selectedValues,
  bulkEmailSlider,
  selectedUsers,
}) => {
  const { multipleJourniesSelected } = useSelector((state: RootState) => state.dashboard);
  const { adminDashboardFilters, emailTemplates } = useSelector((state: RootState) => state.oneOnboarding);

  const storeDispatch = useDispatch();
  const { sites } = useSiteOptions(filterControls.isActive);
  const { t } = useTranslation(['recruiter']);

  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(MailHistoryType.AUTOMATIC);
  const initialSelectedMailType: {
    mailTemplateId: null | string;
    id: null | string;
  } = {
    mailTemplateId: null,
    id: null,
  };
  const [selectedMailType, setSelectedMailType] = useState(initialSelectedMailType);
  const [, setShowCustomEmail] = useState<boolean>(true);
  const [mailSubject, setMailSubject] = useState('');
  const [mailBody, setMailBody] = useState('');

  const searchString = useMemo(() => serializeFilters(adminDashboardFilters), [adminDashboardFilters]);
  const aamBackendApi = useInjection<AamBackendApi>(TYPES.AamBackendApi);

  useUpdateQueryStringValueWithoutNavigation(searchString);
  useUpdateAdminDashboardFilters(selectedValues, adminDashboardFilters, sites, selectedConfiguration, storeDispatch);

  const resetSelections = () => {
    setActiveTab(MailHistoryType.AUTOMATIC);
    setSelectedMailType(initialSelectedMailType);
    setShowCustomEmail(true);
    setMailSubject('');
    setMailBody('');
  };

  useEffect(() => {
    if (!bulkEmailSlider) {
      resetSelections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkEmailSlider]);

  useEffect(() => {
    setSelectedMailType(initialSelectedMailType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multipleJourniesSelected]);

  const handleClose = () => {
    resetSelections();
    filterControls.onClose();
  };

  const journeysOptions = useMemo(() => {
    if (selectedValues?.clientOrganizationId !== '') {
      return allJourneys
        ?.filter((x) => JSON.parse(x.id).clientOrganizationId === selectedUsers[0]?.clientOrganizationId)
        .map((a: any) => ({
          id: a.id,
          name: a.recruiterName,
          value: a.id,
        }));
    }
    return allJourneys?.map((a: any) => ({
      id: a.id,
      name: a.recruiterName,
      value: a.id,
    }));
  }, [allJourneys, selectedValues?.clientOrganizationId, selectedUsers]);

  useEffect(() => {
    if (allClientOrganizations.length === 1) {
      onClientOrganizationChange(allClientOrganizations[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allClientOrganizations]);

  useEffect(() => {
    if (journeysOptions?.length === 1) {
      onJourneyChange(journeysOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeysOptions]);

  useEffect(() => {
    (async () => {
      if (selectedUsers.length) {
        const mailList = await aamBackendApi.fetchSelectedMail(
          selectedUsers[0]?.clientOrganizationId,
          selectedUsers[0]?.configurationId,
        );
        storeDispatch(oneOnboardingSlice.actions.setMails(mailList));
      }
    })();
    aamBackendApi.getEmailTemplates().then((data) => {
      storeDispatch(oneOnboardingSlice.actions.setEMailTemplates(data));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsers]);

  const getSelectedMailContent = () => {
    const filteredEmail = emailTemplates.find((item) => item.name === selectedMailType.mailTemplateId);
    return filteredEmail?.body ?? '';
  };

  const sendBulkMailHandler = async () => {
    if (selectedMailType?.mailTemplateId && selectedMailType?.id) {
      const selectedCandidates = selectedUsers.map((el: any) => ({
        clientOrganizationId: el.clientOrganizationId,
        configurationId: el.configurationId,
        userId: el.userId,
        applicationId: el.applicationId,
      }));
      const requestData: bulkMailPayload = {
        selectedCandidates: selectedCandidates,
        ruleType: activeTab,
        mailTemplateId: selectedMailType?.mailTemplateId,
        id: selectedMailType?.id,
        mailSubject: mailSubject ? mailSubject : '',
        mailBody: mailBody ? mailBody : '',
      };
      try {
        setIsLoading(true);
        aamBackendApi.sendBulkMail(requestData);
        const bulkMailInitiationMessage: string = t('recruiter:GENERAL.MAILS.bulkMailInitiationMessage');
        setIsLoading(false);
        storeDispatch(
          snackbarSlice.actions.showSuccess({
            message: bulkMailInitiationMessage,
          }),
        );
        handleClose();
      } catch (err) {
        setIsLoading(false);
        storeDispatch(
          snackbarSlice.actions.showError({
            message: err?.message ?? t('recruiter:GENERAL.MAILS.bulkMailFailureMessage'),
          }),
        );
      }
    }
  };

  return (
    <>
      <Drawer open={bulkEmailSlider} anchor="right" onClose={handleClose}>
        {isLoading && <Loader size={24} />}
        <Box display="flex" mb={4} alignItems="center" style={{ borderBottom: '1px solid #ebebee' }}>
          <IconButton onClick={handleClose}>
            <CloseRoundedIcon color="primary" />
          </IconButton>
          <p className="tag-ds large">{t('recruiter:GENERAL.MAILS.sendBulkMailHeader')}</p>
        </Box>
        <div style={{ height: '100%', display: 'flex', flexDirection: 'row', marginTop: '-20px', minWidth: '700px' }}>
          <div className="admin app" style={{ minWidth: '400px' }}>
            <Grid container className="feature-mail-overview__container" style={{ height: '100%' }}>
              <Grid item xs={12} style={{ width: '100%' }} className="feature-mail-overview__left-side">
                <BulkMailList
                  selectedMailType={selectedMailType?.mailTemplateId ? selectedMailType : initialSelectedMailType}
                  setSelectedMailType={setSelectedMailType}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  setShowCustomEmail={setShowCustomEmail}
                  multipleJourniesSelected={multipleJourniesSelected}
                  mailSubject={mailSubject}
                  setMailSubject={setMailSubject}
                  mailBody={mailBody}
                  setMailBody={setMailBody}
                />
                <div
                  className="tag-ds feature-mail-overview__send-button-container"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-around',
                    alignContent: 'center',
                    justifyItems: 'center',
                  }}
                >
                  <button
                    className="feature-mail-overview__send-button"
                    style={{
                      maxWidth: '80%',
                    }}
                    onClick={sendBulkMailHandler}
                    disabled={isLoading}
                  >
                    {t('recruiter:GENERAL.MAILS.sendEmail')}
                  </button>
                </div>
              </Grid>
            </Grid>
          </div>
          <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
            {activeTab === MailHistoryType.CUSTOM ? (
              <Grid item md={6} sx={{ height: '100%', overflowY: 'auto' }}>
                <EmailPlaceholderList />
              </Grid>
            ) : (
              <Grid item md={6} sx={{ height: '100%', overflowY: 'auto' }}>
                <h2 style={{ textAlign: 'center' }}>{t('recruiter:GENERAL.MAILS.emailPreview')}</h2>
                <MailPreview content={getSelectedMailContent() || ''} />
              </Grid>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};
